import type { MessageTemplate, MessageTemplateResponse } from '../../../lib/shared/types';
import type { ReplacementTarget, ReplacementComparison } from '../../../lib/shared/types';
import { PlanningLifecycleCatalog } from '../types/planningLifecycle';
import { CongregationDefaults } from '../types/congregationSettings';
import { MidweekWorkbookImportRequest, MidweekWorkbookReview } from '../types/midweek';
import {
  CycleConfigurations,
  CycleConfigurationSelection,
  WeekConfigurationUpdate,
} from '../types/weekConfiguration';
// API client with automatic token management and refresh
import { environment } from '../config/environment';
import { refreshAuthSession, SessionExpiredError } from './authSession';
import { translations } from '../i18n/translations';
import {
  AbsenceListResponse,
  AbsenceConflictOverview,
  CreateAbsenceRequest,
  CreateAbsenceBatchRequest,
  CreateAbsenceBatchResponse,
  CreateAbsenceResponse,
  AbsenceResponseCycles,
  AbsenceResponseOverview,
} from '../types/absence';
import { CreatePublisherRequest, UpdatePublisherRequest } from '../types/publisher';

export interface PublisherCalendarLink {
  version: string | null;
  url: string | null;
  revoked: boolean;
}

interface ApiResponse<T = unknown> {
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

const retrySourceConflict = async <T>(
  request: () => Promise<ApiResponse<T>>
): Promise<ApiResponse<T>> => {
  for (const delay of [500, 1000, 2000, 4000, 8000, 8000]) {
    const result = await request();
    if (result.error?.code !== 'SOURCE_CHANGED') return result;
    await new Promise(resolve => setTimeout(resolve, delay));
  }
  return request();
};

class ApiClient {
  private baseURL: string;

  getPlanningCycleOptions() {
    return this.get<import('../types/planningLifecycle').PlanningCycleOptions>(
      '/week-configurations?view=cycle-options'
    );
  }

  getPlanningCycles(year: string, scopeId: string) {
    return this.get<PlanningLifecycleCatalog>(
      `/week-configurations?view=lifecycle&year=${encodeURIComponent(year)}&scopeId=${encodeURIComponent(scopeId)}`
    );
  }

  constructor(baseURL: string = environment.apiUrl) {
    this.baseURL = baseURL.replace(/\/+$/, '');
  }

  private getAuthToken(endpoint: string): string | null {
    if (endpoint.startsWith('/absences') || endpoint === '/auth/validate') {
      return localStorage.getItem('auth_token');
    }
    return localStorage.getItem('id_token') || localStorage.getItem('auth_token');
  }

  private async refreshToken(): Promise<string> {
    if (environment.mockAuth) {
      const existingToken = localStorage.getItem('auth_token');
      if (!existingToken) {
        throw new Error('No mock session available');
      }

      const payload = JSON.parse(
        atob(existingToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
      );
      const nextTokenPayload = {
        ...payload,
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor((Date.now() + 60 * 60 * 1000) / 1000),
      };
      const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/g, '');
      const body = btoa(JSON.stringify(nextTokenPayload))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/g, '');
      const token = `${header}.${body}.mock-signature`;
      localStorage.setItem('auth_token', token);
      return token;
    }

    return refreshAuthSession();
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount: number = 0
  ): Promise<ApiResponse<T>> {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${this.baseURL}${normalizedEndpoint}`;
    const token = this.getAuthToken(normalizedEndpoint);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // If token expired, try to refresh and retry (only once)
      if (response.status === 401 && token && retryCount === 0) {
        try {
          const newToken = await this.refreshToken();
          headers.Authorization = `Bearer ${newToken}`;

          // Retry the request with new token
          return await this.makeRequest(endpoint, options, retryCount + 1);
        } catch (refreshError) {
          if (!(refreshError instanceof SessionExpiredError)) {
            return {
              error: {
                code: 'NETWORK_ERROR',
                message:
                  translations[localStorage.getItem('congplanner_language') === 'en' ? 'en' : 'de'][
                    'protected.sessionRetryDescription'
                  ],
              },
            };
          }
          if (!localStorage.getItem('auth_token') && !window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }

          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'Session expired. Please log in again.',
            },
          };
        }
      }

      const contentType = response.headers?.get?.('content-type');
      let data;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        let errorMessage = 'Request failed';

        if (typeof data === 'object' && data.error?.message) {
          errorMessage = data.error.message;
        } else if (typeof data === 'object' && data.message) {
          errorMessage = data.message;
        } else if (typeof data === 'string') {
          errorMessage = data;
        }

        return {
          error: {
            code:
              typeof data === 'object' && data.error?.code
                ? data.error.code
                : response.status.toString(),
            message: errorMessage,
            details: typeof data === 'object' ? data.details : undefined,
          },
        };
      }

      const normalizedData =
        data && typeof data === 'object' && 'success' in data && 'data' in data
          ? (data as { data: T }).data
          : data;

      return { data: normalizedData };
    } catch (error) {
      let errorMessage = 'Network error occurred';

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Request was cancelled';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to server. Please check your internet connection.';
        } else {
          errorMessage = error.message;
        }
      }

      return {
        error: {
          code: 'NETWORK_ERROR',
          message: errorMessage,
        },
      };
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();

const cleanQueryParams = (params?: Record<string, unknown>) => {
  if (!params) return undefined;

  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );
};

const toSearchParams = <T extends object>(params: T) =>
  new URLSearchParams(
    Object.entries(params).map(([key, value]) => [key, String(value)] as [string, string])
  );

// Convenience functions for common API calls
export const authApi = {
  login: (email: string, password: string) => apiClient.post('/auth/login', { email, password }),

  forgotPassword: (email: string) => apiClient.post('/auth/forgot-password', { email }),

  confirmForgotPassword: (data: { email: string; code: string; newPassword: string }) =>
    apiClient.post('/auth/confirm-forgot-password', data),

  refresh: (refreshToken: string) => apiClient.post('/auth/refresh', { refreshToken }),

  generateDeepLink: (publisherId: string) => apiClient.post('/auth/deep-link', { publisherId }),

  validateDeepLink: (token: string) => apiClient.post('/auth/validate-deep-link', { token }),

  validateToken: () => apiClient.get('/auth/validate'),

  listUsers: () => apiClient.get('/auth/users'),

  createUser: (data: {
    email: string;
    givenName: string;
    familyName: string;
    congregation?: string;
    appRole?: string;
  }) => apiClient.post('/auth/users', data),

  updateUser: (
    username: string,
    data: {
      email?: string;
      givenName?: string;
      familyName?: string;
      congregation?: string;
      enabled?: boolean;
    }
  ) => apiClient.put(`/auth/users/${encodeURIComponent(username)}`, data),

  updateUserRole: (username: string, appRole: string) =>
    apiClient.put(`/auth/users/${encodeURIComponent(username)}/role`, { appRole }),
};

export const midweekApi = {
  importWorkbook: (data: MidweekWorkbookImportRequest) =>
    apiClient.post<{
      import:
        | MidweekWorkbookReview
        | {
            mode: 'imported' | 'applied';
            weeksImported: number;
            updatedRowCount?: number;
          };
      payload?: { weeks?: import('../types/midweek').MidweekWeekSummary[] };
    }>('/midweek/import', data),

  listWeeks: (params: { year: number; workbook: number; includeParticipants?: boolean }) =>
    apiClient.get<import('../types/midweek').MidweekWeeksResponse>(
      `/midweek/weeks?${new URLSearchParams({ year: String(params.year), workbook: String(params.workbook), ...(params.includeParticipants ? { includeParticipants: 'true' } : {}) })}`
    ),

  getWeek: (meetingDate: string) => apiClient.get(`/midweek/weeks/${meetingDate}`),

  updateItem: (
    meetingDate: string,
    programOrder: number,
    data: {
      assigneeId?: string | null;
      assistantId?: string | null;
      status?: string | null;
    }
  ) => apiClient.put(`/midweek/weeks/${meetingDate}/items/${programOrder}`, data),

  listWeekCandidates: (meetingDate: string) =>
    apiClient.get<import('../types/midweek').MidweekWeekCandidates>(
      `/midweek/weeks/${meetingDate}/candidates`
    ),

  listCandidates: (meetingDate: string, programOrder: number) =>
    apiClient.get(`/midweek/weeks/${meetingDate}/items/${programOrder}/candidates`),
};

export const weekConfigurationApi = {
  cycleConfigurations: (selection: CycleConfigurationSelection) =>
    apiClient.get<CycleConfigurations>(
      `/week-configurations?${toSearchParams({ view: 'cycle-configurations', ...selection })}`
    ),
  cycleWeeks: (year: number, workbook: number) =>
    apiClient.get(`/week-configurations?year=${year}&workbook=${workbook}`),
  get: (params: {
    weekStartDate: string;
    weekEndDate?: string;
    meetingDate?: string;
    weekendMeetingDate?: string;
  }) => {
    const queryParams = cleanQueryParams({
      includeSchedule: 'true',
      weekEndDate: params.weekEndDate,
      meetingDate: params.meetingDate,
      weekendMeetingDate: params.weekendMeetingDate,
    });

    return apiClient.get(
      `/week-configurations/${params.weekStartDate}${queryParams ? `?${toSearchParams(queryParams)}` : ''}`
    );
  },

  update: (weekStartDate: string, data: WeekConfigurationUpdate) =>
    apiClient.put(`/week-configurations/${weekStartDate}`, data),
};

export const congregationSettingsApi = {
  get: () => apiClient.get('/congregation-settings'),
  save: (defaults: CongregationDefaults, expectedVersion: string) =>
    apiClient.put('/congregation-settings', { ...defaults, expectedVersion }),
};

export const publicPlanAccessApi = {
  get: () =>
    apiClient.get<{ access: import('../types/congregationSettings').PublicPlanAccessStatus }>(
      '/congregation-settings?view=public-plan-access'
    ),
  save: (password: string, expectedVersion: string) =>
    apiClient.put<{ access: import('../types/congregationSettings').PublicPlanAccessStatus }>(
      '/congregation-settings?view=public-plan-access',
      { password, expectedVersion }
    ),
};

export const servicePlanningApi = {
  assignmentOptions: (
    scopeId: string,
    slot: { assignmentDate: string; serviceRole: string; slotId: string }
  ) =>
    apiClient.get(
      `/service-scopes/${encodeURIComponent(scopeId)}/assignment-options?${new URLSearchParams(slot).toString()}`
    ),
  assignmentState: (scopeId: string) =>
    apiClient.get(`/service-scopes/${encodeURIComponent(scopeId)}/assignment-state`),
  undoAutomatic: (
    scopeId: string,
    data: import('../types/planning').ServiceAutomaticRequest & { runId: string }
  ) => apiClient.post(`/service-scopes/${encodeURIComponent(scopeId)}/undo-auto-assignment`, data),
  autoAssign: (scopeId: string, data: import('../types/planning').ServiceAutomaticRequest) =>
    apiClient.post(`/service-scopes/${encodeURIComponent(scopeId)}/auto-assign`, data),
  refreshInput: (scopeId: string, data: import('../types/planning').ServiceAutomaticRequest) =>
    apiClient.post(`/service-scopes/${encodeURIComponent(scopeId)}/refresh-assignment-input`, data),
  candidates: (
    scopeId: string,
    slot: { assignmentDate: string; serviceRole: string; slotId: string }
  ) =>
    apiClient.get(
      `/service-scopes/${encodeURIComponent(scopeId)}/assignment-candidates?${new URLSearchParams(slot).toString()}`
    ),
  returnToAutomatic: (
    scopeId: string,
    assignmentDate: string,
    serviceRole: string,
    slotId: string
  ) =>
    apiClient.put(
      `/service-assignments/${encodeURIComponent(assignmentDate)}/${encodeURIComponent(serviceRole)}/${encodeURIComponent(slotId)}?scopeId=${encodeURIComponent(scopeId)}`,
      { returnToAutomatic: true }
    ),
  watchtowerContext: (scopeId: string) =>
    apiClient.get(`/service-scopes/${encodeURIComponent(scopeId)}/watchtower-context`),
  defaults: () => apiClient.get('/service-planning-defaults'),
  saveDefaults: (
    watchtowerStudyDefaultPublisherId: string | null,
    expectedVersion: string | null
  ) =>
    apiClient.put('/service-planning-defaults', {
      watchtowerStudyDefaultPublisherId,
      expectedVersion,
    }),
  useDefault: (scopeId: string, assignmentDate: string) =>
    apiClient.put(
      `/service-assignments/${encodeURIComponent(assignmentDate)}/WatchtowerStudy/main?scopeId=${encodeURIComponent(scopeId)}`,
      { useDefault: true }
    ),
  scope: (scopeId: string) => apiClient.get(`/service-scopes/${encodeURIComponent(scopeId)}`),
  saveScope: (
    scopeId: string,
    slots: Array<{ assignmentDate: string; serviceRole: string; slotId: string }>,
    expectedVersion: string | null,
    expectedSettingsVersion?: string
  ) =>
    apiClient.put(`/service-scopes/${encodeURIComponent(scopeId)}`, {
      slots,
      expectedVersion,
      ...(expectedSettingsVersion ? { expectedSettingsVersion } : {}),
    }),
  list: (startDate: string, endDate: string) =>
    apiClient.get(`/service-assignments?${new URLSearchParams({ startDate, endDate }).toString()}`),
  save: (
    scopeId: string,
    assignmentDate: string,
    serviceRole: string,
    slotId: string,
    data: { publisherId: string; status?: string }
  ) =>
    retrySourceConflict(() =>
      apiClient.put(
        `/service-assignments/${encodeURIComponent(assignmentDate)}/${encodeURIComponent(serviceRole)}/${encodeURIComponent(slotId)}?scopeId=${encodeURIComponent(scopeId)}`,
        data
      )
    ),
  unassign: (scopeId: string, assignmentDate: string, serviceRole: string, slotId: string) =>
    apiClient.delete(
      `/service-assignments/${encodeURIComponent(assignmentDate)}/${encodeURIComponent(serviceRole)}/${encodeURIComponent(slotId)}?scopeId=${encodeURIComponent(scopeId)}`
    ),
};

export const planPublicationApi = {
  replace: (
    target: ReplacementTarget,
    expectedVersion: number,
    change: { action: 'confirm'; publisherId: string } | { action: 'undo' }
  ) =>
    apiClient.post<{ replacement: { version: number } }>(
      `/plans/${target.planType}/${encodeURIComponent(target.scopeId)}/publications?view=replacement`,
      {
        revision: target.revision,
        assignmentKey: target.assignmentKey,
        responsibility: target.responsibility,
        expectedVersion,
        ...change,
      }
    ),
  replacementOptions: (target: ReplacementTarget) =>
    apiClient.get<{ comparison: ReplacementComparison }>(
      `/plans/${target.planType}/${encodeURIComponent(target.scopeId)}/publications/${target.revision}?${new URLSearchParams({ view: 'replacement-options', assignmentKey: target.assignmentKey, responsibility: target.responsibility })}`
    ),
  freezeCycle: (
    scopeId: string,
    draftVersion: string,
    idempotencyKey: string,
    reviews: { lamm: boolean; service: boolean }
  ) =>
    apiClient.post(`/plans/cycle/${encodeURIComponent(scopeId)}/publications`, {
      draftVersion,
      idempotencyKey,
      reviews,
    }),
  readiness: (planType: string, scopeId: string) =>
    apiClient.get(
      `/plans/${encodeURIComponent(planType)}/${encodeURIComponent(scopeId)}/readiness`
    ),
  publish: (planType: string, scopeId: string, draftVersion: string, idempotencyKey: string) =>
    apiClient.post(
      `/plans/${encodeURIComponent(planType)}/${encodeURIComponent(scopeId)}/publications`,
      {
        draftVersion,
        idempotencyKey,
      }
    ),
  current: (planType: string, scopeId: string, includeOverview = false, effective = false) =>
    apiClient.get(
      `/plans/${encodeURIComponent(planType)}/${encodeURIComponent(scopeId)}/publications/current${effective ? `?view=effective${includeOverview ? '&includeOverview=true' : ''}` : includeOverview ? '?includeOverview=true' : ''}`
    ),
  state: (planType: string, scopeId: string) =>
    apiClient.get(`/plans/${encodeURIComponent(planType)}/${encodeURIComponent(scopeId)}/state`),
  unlock: (planType: string, scopeId: string) =>
    apiClient.post(
      `/plans/${encodeURIComponent(planType)}/${encodeURIComponent(scopeId)}/state/unlock`,
      {}
    ),
  revision: (planType: string, scopeId: string, revision: number, includeOverview = false) =>
    apiClient.get(
      `/plans/${encodeURIComponent(planType)}/${encodeURIComponent(scopeId)}/publications/${revision}${includeOverview ? '?includeOverview=true' : ''}`
    ),
};

export const publishersApi = {
  list: (params: {
    search?: string;
    publisherType?: string;
    congregationRole?: string;
    isActive: boolean;
    lastEvaluatedKey?: string;
    limit?: number;
    offset?: number;
  }) => {
    const queryParams = cleanQueryParams({
      ...params,
      type: params.publisherType,
      role: params.congregationRole,
      active: params.isActive,
    });

    if (queryParams) {
      delete (queryParams as { publisherType?: string }).publisherType;
      delete (queryParams as { congregationRole?: string }).congregationRole;
      delete (queryParams as { isActive?: boolean }).isActive;
    }

    return apiClient.get(`/publishers${queryParams ? `?${toSearchParams(queryParams)}` : ''}`);
  },

  calendar: (id: string) =>
    apiClient.get<PublisherCalendarLink>(`/publishers/${encodeURIComponent(id)}/calendar`),
  changeCalendar: (id: string, action: 'create' | 'rotate' | 'revoke', version: string | null) =>
    apiClient.post<PublisherCalendarLink>(`/publishers/${encodeURIComponent(id)}/calendar`, {
      action,
      version,
    }),

  summary: () => apiClient.get('/publishers?summary=true'),

  get: (id: string) => apiClient.get(`/publishers/${id}`),

  create: (publisher: CreatePublisherRequest) => apiClient.post('/publishers', publisher),

  importJson: (document: unknown) => apiClient.post('/publishers/import', { document }),

  update: (id: string, publisher: UpdatePublisherRequest) =>
    apiClient.put(`/publishers/${id}`, publisher),

  delete: (id: string) => apiClient.delete(`/publishers/${id}`),
};

export const absencesApi = {
  conflicts: () => apiClient.get<AbsenceConflictOverview>('/absences?view=conflicts'),
  responseCycles: () => apiClient.get<AbsenceResponseCycles>('/absences?view=response-cycles'),
  responses: (cycleId: string) =>
    apiClient.get<AbsenceResponseOverview>(
      `/absences?view=responses&cycleId=${encodeURIComponent(cycleId)}`
    ),
  confirmNoAbsences: (cycleId: string, publisherId: string) =>
    apiClient.put(
      `/absences?view=responses&cycleId=${encodeURIComponent(cycleId)}&publisherId=${encodeURIComponent(publisherId)}`,
      { confirmedNoAbsences: true }
    ),
  undoNoAbsences: (cycleId: string, publisherId: string) =>
    apiClient.put(
      `/absences?view=responses&cycleId=${encodeURIComponent(cycleId)}&publisherId=${encodeURIComponent(publisherId)}`,
      { confirmedNoAbsences: false }
    ),
  list: (params?: {
    publisherId?: string;
    startDate?: string;
    endDate?: string;
    absenceType?: string;
    publisherGroups?: import('../types/absence').PublisherGroup[];
    lastEvaluatedKey?: string;
    limit?: number;
    offset?: number;
  }) => {
    const queryParams = cleanQueryParams(
      params
        ? {
            ...params,
            type: params.absenceType,
            publisherGroups: params.publisherGroups?.length
              ? [...new Set(params.publisherGroups)].join(',')
              : undefined,
          }
        : undefined
    );

    if (queryParams) {
      delete (queryParams as { absenceType?: string }).absenceType;
    }

    return apiClient.get<AbsenceListResponse>(
      `/absences${queryParams ? `?${toSearchParams(queryParams)}` : ''}`
    );
  },

  get: (id: string) => apiClient.get(`/absences/${id}`),

  create: (absence: CreateAbsenceRequest) =>
    apiClient.post<CreateAbsenceResponse>('/absences', absence),

  createBatch: (absence: CreateAbsenceBatchRequest) =>
    apiClient.post<CreateAbsenceBatchResponse>('/absences', absence),

  importCsv: (csvContent: string) => apiClient.post('/absences/import', { csvContent }),

  update: (id: string, absence: CreateAbsenceRequest) => apiClient.put(`/absences/${id}`, absence),

  delete: (id: string) => apiClient.delete(`/absences/${id}`),
};

export const exportApi = {
  generate: (params: {
    format: 'csv' | 'json' | 'excel';
    startDate?: string;
    endDate?: string;
    publisherId?: string;
    absenceType?: string;
  }) => apiClient.post('/export', params),

  list: (params?: { limit?: number; offset?: number }) =>
    apiClient.get(`/export/history${params ? `?${toSearchParams(params)}` : ''}`),

  get: (id: string) => apiClient.get(`/export/${id}`),

  download: (id: string) => apiClient.get(`/export/${id}/download`),
};

const artifactPath = (planType: string, scopeId: string, revision: number) =>
  `/plans/${encodeURIComponent(planType)}/${encodeURIComponent(scopeId)}/publications/${revision}/artifacts`;

export const planArtifactsApi = {
  calendar: (
    selections: import('../types/planning').DistributionSelection[],
    publisherId: string
  ) => apiClient.post('/plan-artifact-downloads/calendar', { selections, publisherId }),
  status: (planType: string, scopeId: string, revision: number, includePersonalFiles = true) =>
    apiClient.get(
      `${artifactPath(planType, scopeId, revision)}${includePersonalFiles ? '' : '?includePersonalFiles=false'}`
    ),
  generate: (planType: string, scopeId: string, revision: number, locale: 'de', retry = false) =>
    apiClient.post(`${artifactPath(planType, scopeId, revision)}${retry ? '/retry' : ''}`, {
      locale,
    }),
  download: (
    planType: string,
    scopeId: string,
    revision: number,
    setId: string,
    artifactId: string,
    preview = false
  ) =>
    apiClient.get(
      `${artifactPath(planType, scopeId, revision)}/sets/${encodeURIComponent(setId)}/${encodeURIComponent(artifactId)}/download${preview ? '?preview=true' : ''}`
    ),
};

const cycleCalendarPath = (cycleId: string) =>
  `/plan-cycle-calendars/${encodeURIComponent(cycleId)}/artifacts`;

export const planCycleCalendarApi = {
  status: (cycleId: string, includePersonalFiles = true) =>
    apiClient.get(
      `${cycleCalendarPath(cycleId)}${includePersonalFiles ? '' : '?includePersonalFiles=false'}`
    ),
  generate: (
    cycleId: string,
    selections: import('../types/planning').DistributionSelection[],
    retry = false
  ) =>
    apiClient.post(`${cycleCalendarPath(cycleId)}${retry ? '/retry' : ''}`, {
      locale: 'de',
      selections,
    }),
  download: (cycleId: string, setId: string, artifactId: string) =>
    apiClient.get(
      `${cycleCalendarPath(cycleId)}/sets/${encodeURIComponent(setId)}/${encodeURIComponent(artifactId)}/download`
    ),
};

export const planDistributionApi = {
  preview: (
    selections: import('../types/planning').DistributionSelection[],
    publisherIds: string[],
    locale: 'de',
    calendarSetId?: string
  ) =>
    apiClient.post('/plan-distributions/preview', {
      selections,
      publisherIds,
      locale,
      calendarSetId,
    }),
  send: (previewId: string) => apiClient.post('/plan-distributions', { previewId, confirm: true }),
  status: (batchId: string) => apiClient.get(`/plan-distributions/${encodeURIComponent(batchId)}`),
  retry: (batchId: string, publisherIds: string[]) =>
    apiClient.post(`/plan-distributions/${encodeURIComponent(batchId)}/retry`, { publisherIds }),
};

export const messageTemplateApi = {
  get: () => apiClient.get<MessageTemplateResponse>('/congregation-settings?view=message-template'),
  save: (template: MessageTemplate) =>
    apiClient.put<MessageTemplateResponse>(
      '/congregation-settings?view=message-template',
      template
    ),
};

export const weeklyCopyPreferenceApi = {
  get: () =>
    apiClient.get<import('../../../lib/shared/types').WeeklyCopyPreferenceResponse>(
      '/congregation-settings?view=weekly-copy-preferences'
    ),
  save: (preference: import('../../../lib/shared/types').WeeklyCopyPreference) =>
    apiClient.put<import('../../../lib/shared/types').WeeklyCopyPreferenceResponse>(
      '/congregation-settings?view=weekly-copy-preferences',
      preference
    ),
};

export const fieldServiceApi = {
  readiness: (cycleId: string) =>
    apiClient.get<import('../types/fieldService').FieldServiceReleaseReview>(
      '/field-service/periods/' + encodeURIComponent(cycleId) + '/readiness'
    ),
  release: (
    cycleId: string,
    body: { reviewVersion: string; acknowledged: true; idempotencyKey: string }
  ) =>
    apiClient.post<{ release: import('../types/fieldService').FieldServiceReleaseState }>(
      '/field-service/periods/' + encodeURIComponent(cycleId) + '/release',
      body
    ),
  unlock: (cycleId: string, expectedRevision: number) =>
    apiClient.post<{ release: import('../types/fieldService').FieldServiceReleaseState }>(
      '/field-service/periods/' + encodeURIComponent(cycleId) + '/release/unlock',
      { expectedRevision }
    ),
  period: (cycleId: string) =>
    apiClient.get<import('../types/fieldService').FieldServicePeriod>(
      '/field-service/periods/' + encodeURIComponent(cycleId)
    ),
  save: (monday: string, draft: import('../types/fieldService').FieldServiceSave) =>
    apiClient.put<{ week: import('../types/fieldService').FieldServiceWeek }>(
      '/field-service/weeks/' + monday,
      draft
    ),
  preview: (monday: string) =>
    apiClient.post<{
      week: import('../types/fieldService').FieldServiceWeek;
      settingsVersion: string;
    }>('/field-service/weeks/' + monday + '/blueprint-preview', {}),
  candidates: (monday: string, meetingId: string, date: string, cycleId: string) =>
    apiClient.get<{ candidates: import('../types/fieldService').FieldServiceCandidate[] }>(
      '/field-service/weeks/' +
        monday +
        '/meetings/' +
        encodeURIComponent(meetingId) +
        '/candidates?date=' +
        date +
        '&cycleId=' +
        cycleId
    ),
  places: (query?: string) =>
    apiClient.get<{ places: import('../types/fieldService').FieldServicePlace[] }>(
      query === undefined
        ? '/field-service/places/recent'
        : '/field-service/places?q=' + encodeURIComponent(query)
    ),
  createPlace: (place: Omit<import('../types/fieldService').FieldServicePlace, 'placeId'>) =>
    apiClient.post<{ place: import('../types/fieldService').FieldServicePlace }>(
      '/field-service/places',
      place
    ),
};
